import axios, { type AxiosResponse } from 'axios';
import type { GICSCode } from '@shared/domain/company/GICS.types.js';
import { authService } from '@shared/services/AuthService';

/**
 *
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await authService.refreshSession();
    return { Authorization: session.getIdToken().getJwtToken() };
  } catch {
    return {};
  }
}

export interface SearchCompaniesDTO {
  approvedOnly?: boolean; // filter by approval status
  companyId?: number[]; // filter by company IDs
  gicsCode?: string[]; // filter by GICS code (prefixes OK too)
  gicsIndustry?: string[]; // filter by GICS industry
  gicsIndustryGroup?: string[]; // filter by GICS industry group
  gicsSector?: string[]; // filter by GICS sector
  gicsSubIndustry?: string[]; // filter by GICS sub-industry
  limit?: number;
  offset?: number; // the offset for pagination
  orderBy?: string; // the field to order by
  orderDirection?: string; // the direction to order by
  searchTerm?: string;
  tagId?: number[]; // tag IDs to filter by
}

export interface SearchCompaniesResponse {
  meta: {
    hasMore: boolean;
    limit: number;
    offset: number;
    total: number;
  };
  results: any[];
}

export const fetchCompanies = async (dto: SearchCompaniesDTO): Promise<SearchCompaniesResponse> => {
  const url = `${import.meta.env.VITE_API_BASE_URL}/company`;
  const headers = await getAuthHeaders();

  try {
    const response: AxiosResponse = await axios.get(url, { headers, params: dto });
    return {
      meta: response.data.meta,
      results: response.data.results,
    };
  } catch {
    throw new Error('Error fetching companies');
  }
};

export const fetchAllGicsCodes = async (codePrefix?: string): Promise<GICSCode[]> => {
  const url = `${import.meta.env.VITE_API_BASE_URL}/gics`;

  try {
    const response: AxiosResponse = await axios.get(url, { params: { codePrefix } });
    return response.data.data || [];
  } catch {
    throw new Error('Error fetching company industries');
  }
};

export const fetchCompanyIndustries = async (): Promise<string[]> => {
  const url = `${import.meta.env.VITE_API_BASE_URL}/company/industries`;

  try {
    const response: AxiosResponse = await axios.get(url);
    return response.data.data || [];
  } catch {
    throw new Error('Error fetching company industries');
  }
};
